import { ATTACHMENT_MAX_BYTES, type AttachmentEntity, type Permission } from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, ImageIcon, Paperclip, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { api, ApiError, downloadFile, uploadFile } from '../api/client';
import { useAuth } from '../features/auth/AuthProvider';
import { formatDateTime } from '../utils/format';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

const size = (b: number) =>
  b < 1024 * 1024
    ? `${Math.max(1, Math.round(b / 1024))} KB`
    : `${(b / 1024 / 1024).toFixed(1)} MB`;

/** Files of one record (invoices, documents): upload (PDF/PNG/JPG/WEBP, ≤ 10 MB), download, delete. */
export function AttachmentsPanel({
  entityType,
  entityId,
  onChange,
  title = 'Invoice attachments',
  writePermission = 'transactions.write',
  bare = false,
}: {
  entityType: AttachmentEntity;
  entityId: string;
  onChange?: () => void;
  title?: string;
  writePermission?: Permission;
  /** Without the top divider (when the panel is a card's whole body). */
  bare?: boolean;
}) {
  const { can } = useAuth();
  const canWrite = can(writePermission);
  const toast = useToast();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const key = ['attachments', entityType, entityId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api.get<Attachment[]>('/attachments', { entityType, entityId }),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    onChange?.();
  };

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.set('entityType', entityType);
      form.set('entityId', entityId);
      form.set('file', file);
      return uploadFile<Attachment>('/attachments', form);
    },
    onSuccess: (a) => {
      setError(null);
      toast.success(`${a.fileName} attached`);
      refresh();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? (err.errors[0]?.message ?? err.message) : 'Upload failed'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/attachments/${id}`),
    onSuccess: refresh,
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  const pick = (file: File | undefined) => {
    if (!file) return;
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setError('Files can be at most 10 MB');
      return;
    }
    upload.mutate(file);
  };

  return (
    <div className={bare ? undefined : 'mt-6 border-t border-slate-100 pt-4'}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <Paperclip className="size-4" /> {title}
        </h3>
        {canWrite && (
          <>
            <input
              ref={input}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              className="hidden"
              aria-label={`Choose file for ${title}`}
              onChange={(e) => {
                pick(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button
              size="sm"
              variant="secondary"
              loading={upload.isPending}
              onClick={() => input.current?.click()}
            >
              Attach file
            </Button>
          </>
        )}
      </div>
      {error && (
        <p className="mb-2 text-xs text-rose-600" role="alert">
          {error}
        </p>
      )}
      {list.data && list.data.length === 0 ? (
        <p className="text-xs text-slate-400">No files. PDF, PNG, JPG or WEBP, up to 10 MB.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {list.data?.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              {a.mimeType === 'application/pdf' ? (
                <FileText className="size-4 shrink-0 text-rose-500" />
              ) : (
                <ImageIcon className="size-4 shrink-0 text-sky-500" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-slate-900">{a.fileName}</span>
                <span className="block text-xs text-slate-400">
                  {size(a.sizeBytes)} · {formatDateTime(a.createdAt)}
                </span>
              </span>
              <button
                type="button"
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label={`Download ${a.fileName}`}
                onClick={() =>
                  downloadFile(`/attachments/${a.id}/download`, undefined, a.fileName).catch(
                    (err) => toast.error(err instanceof ApiError ? err.message : 'Download failed'),
                  )
                }
              >
                <Download className="size-4" />
              </button>
              {canWrite && (
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  aria-label={`Delete ${a.fileName}`}
                  onClick={() => remove.mutate(a.id)}
                >
                  <Trash2 className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
